import React, { useState, useMemo, useEffect } from 'react';
import { Dropdown, Layout, Menu, Typography, message } from 'antd';
import propTypes from 'prop-types';
import './authorizedlayout.less';
import { NavLink, withRouter, Redirect } from 'react-router-dom';
import { PATHS }  from '../../constants';
import {
    LineChartOutlined,
    EditOutlined,
    SettingOutlined,
    LogoutOutlined,
    DownOutlined
} from '@ant-design/icons';
import { AUTH_CHECK, GET_DEPARTMENTS } from '../../queries';
import { useQuery } from '@apollo/client';
import { authenticationService } from '../../Utilities/authenticationService';
import {
    getUnauthorizedDepartmentRedirect,
    departmentLabelJa,
    getUserDepartmentNames
} from '../../Utilities/departmentUtils';

const { Header, Content, Sider } = Layout;

const AuthorizedLayout = ({ location, history, children, disableBorder, title }) => {
    const [collapsed, setCollapsed] = useState(false);
    useQuery(AUTH_CHECK, {
       onError: () => {
            authenticationService.logout();
            history.push({
                pathname: PATHS.login,
                state: { from: { location: location.pathname } }
            });
        }
    });
    
    const { data: departmentsData } = useQuery(GET_DEPARTMENTS);

    const departmentAccessRedirect = useMemo(
        () => getUnauthorizedDepartmentRedirect(location.pathname),
        [location.pathname]
    );

    useEffect(() => {
        const attempted = location.state && location.state.departmentAccessDenied;
        if (!attempted) {
            return;
        }
        const label = departmentLabelJa(attempted);
        message.warning(
            `この事業部（${label}）へのアクセス権限がありません。担当の事業部の画面に切り替えました。`
        );
        history.replace({
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            state: {}
        });
    }, [location.state, location.pathname, location.search, location.hash, history]);

    const userDepartmentNames = getUserDepartmentNames();

    // Build a map of department name -> displayName from the API
    const departmentDisplayNames = useMemo(() => {
        const map = {};
        if (departmentsData?.departments) {
            departmentsData.departments.forEach(dept => {
                map[dept.name] = dept.displayName;
            });
        }
        return map;
    }, [departmentsData]);

    if (departmentAccessRedirect) {
        return (
            <Redirect
                to={{
                    pathname: departmentAccessRedirect.redirectTo,
                    state: { departmentAccessDenied: departmentAccessRedirect.attemptedDepartment }
                }}
            />
        );
    }

    const handleLogout = () => {
        authenticationService.logout();
        history.push('/login');
    };

    const UserMenu = <Menu key="user-panel" style={{ background: '#fff' }}>
        <Menu.Item icon={<SettingOutlined />} key="settings"><NavLink to={PATHS.userSettings}>アカウント</NavLink></Menu.Item>
        <Menu.Item icon={<LogoutOutlined />} key='logout' onClick={handleLogout}>ログアウト</Menu.Item>
    </Menu>;

    const Sidebar = <Sider className="site-layout-background site-layout-sidebar" collapsible onCollapse={value => setCollapsed(value)}>
        <img src={require('../../static/ApplepieLogo.svg')} alt='applepielogo' className='app-logo' style={{padding: collapsed && 10}} />
        <Menu
            mode="inline"
            defaultOpenKeys={userDepartmentNames}
            style={{ height: '100%', borderRight: 0 }}
        >
            {userDepartmentNames.map(deptName => (
                <Menu.SubMenu key={deptName} title={departmentDisplayNames[deptName] || deptName}>
                    <Menu.Item key={`${deptName}-estimations`} icon={<LineChartOutlined />}>
                        <NavLink to={`/estimations/${deptName}`}>商品予測</NavLink>
                    </Menu.Item>
                    <Menu.Item key={`${deptName}-addMenu`} icon={<EditOutlined />}>
                        <NavLink to={`/addMenu/${deptName}`}>商品管理</NavLink>
                    </Menu.Item>
                </Menu.SubMenu>
            ))}
        </Menu>
    </Sider>;

    const sidebarWidth = collapsed ? 80 : 200;
    const Topbar = <Header
        className="main-header"
        style={{
            background: '#fff',
            left: sidebarWidth,
            width: `calc(100% - ${sidebarWidth}px)`
        }}
    >
        <Typography.Title level={4} style={{float: "left", marginTop: "0.8em", marginLeft: "0.8em"}}>{title}</Typography.Title>
        <Dropdown className='user-panel' overlay={UserMenu} trigger={['click']}>
            <div>
                { JSON.parse(localStorage.currentUser).last_name } { JSON.parse(localStorage.currentUser).first_name }
                {' '}
                <DownOutlined />
            </div>
        </Dropdown>
    </Header>;

    return <Layout>
        <Layout>
            { Sidebar }
            <Layout style={{marginLeft: collapsed ? 80 : 200}}>
                { Topbar }
                <Content
                    className={ disableBorder ? '' : 'site-layout-background' }
                    style={{
                        margin: 0,
                        minHeight: 280,
                        marginTop: 64,
                        background: '#fff'
                    }}
                >
                    { children }
                </Content>
            </Layout>
        </Layout>
    </Layout>;
};

AuthorizedLayout.propTypes = {
    children: propTypes.element,
    onLogout: propTypes.func,
    location: propTypes.object,
    history: propTypes.object,
    disableBorder: propTypes.bool
};

export default withRouter(AuthorizedLayout);
